import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { InviteService } from "../../../friends/invite_service.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { CurrencyBar } from "../../common_ui_elements/currency_bar.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { UIScrollableContainer } from "../../common_ui_elements/ui_scrollable_container.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { UITileImage } from "../../common_ui_elements/ui_tile_image.js";
import { SafeAreas } from "../../safe_areas.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { OfferPlacement, OfferStore, type OfferDto } from "../../../api/dto/offers.dto.js";
import type { PlayerDto } from "../../../api/dto/player.dto.js";
import { adoptEconomyFromPlayer, refreshPlayerFromServer } from "../../../api/player_sync.js";
import { logVirtualCurrencyEarned } from "../../../analytics/appsflyer_events.js";
import { OfferService } from "../../../offers/offer_service.js";
import { OfferTriggerController } from "../../../offers/offer_trigger_controller.js";
import { TelegramStars } from "../../../plugins/telegram_stars.js";
import { TonConnectFailure, TonPaymentPending, TonPayments } from "../../../plugins/ton_payments.js";
import { TonPaymentSheet } from "../../../plugins/ton_payment_sheet.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { crashCapture } from "../../../crash/crash_reporter.js";

/**
 * The shop: offers come from the server (OfferService), purchases go through
 * OfferService.purchaseOffer, which picks the store for this build (Telegram
 * Stars inside Telegram, TON / USDT through TON Connect on the web).
 */

export class ShopOverlay extends AbstractOverlay
{
  private static readonly BG_COLOR = "#272567";
  private static readonly BOTTOM_FILL_COLOR = "#4541B7";

  private static readonly TOP_IMG_W = 260;
  private static readonly TOP_IMG_H = 292;
  private static readonly TOP_STRIPE_W = 130;
  private static readonly HEADER_Y = 41;
  private static readonly HEADER_H = 432;
  private static readonly CONTENT_TOP = 473;
  private static readonly BOTTOM_H = 304;
  private static readonly CLOSE_SIZE = 130;
  private static readonly CLOSE_CENTER_FROM_BOTTOM = 165;
  private static readonly CURRENCY_SYNC_MS = 500;

  private static readonly WALLET_SIZE = 141;
  private static readonly WALLET_RIGHT_MARGIN = 46;
  private static readonly WALLET_BADGE_W = 62;
  private static readonly WALLET_BADGE_H = 64;
  private static readonly WALLET_BADGE_DX = 55;
  private static readonly WALLET_BADGE_DY = -50;

  private static readonly TAB_W = 513;
  private static readonly TAB_H = 148;
  private static readonly TAB_Y = 296;
  private static readonly TAB_LABEL_Y = 46;
  private static readonly TAB_LABEL_H = 60;
  private static readonly TAB_FONT = 50;

  private static readonly PANEL_Y = 32;
  private static readonly PANEL_W = 900;
  private static readonly PANEL_H = 1085;
  private static readonly FRIENDS_IMG_Y = 40;
  private static readonly FRIENDS_IMG_W = 641;
  private static readonly FRIENDS_IMG_H = 441;
  private static readonly INVITE_TEXT_Y = 432;
  private static readonly INVITE_TEXT_W = 700;
  private static readonly INVITE_TEXT_H = 150;
  private static readonly REWARD_Y = 585;
  private static readonly REWARD_H = 140;
  private static readonly REWARD_TEXT_W = 420;
  private static readonly REWARD_COINS_SIZE = 190;
  private static readonly REWARD_GAP = 10;
  private static readonly INVITE_BTN_Y = 775;
  private static readonly INVITE_BTN_W = 640;
  private static readonly INVITE_BTN_H = 260;
  private static readonly DEFAULT_REWARD_COINS = 5000;

  private static readonly SECTION_HEADER_W = 970;
  private static readonly SECTION_HEADER_H = 100;
  private static readonly STARS_HEADER_Y = 54;
  private static readonly STARS_ROW_Y = 182;
  private static readonly STARS_ROW_W = 984;
  private static readonly STARS_ROW_H = 150;
  private static readonly STARS_ROW_GAP = 18;
  /** Section header → first item, and the gap after a section (design px). */
  private static readonly HEADER_TO_ITEMS = 128;
  private static readonly SECTION_GAP = 49;
  private static readonly EMPTY_TEXT_H = 200;
  private static readonly TON_CARD_W = 317;
  private static readonly TON_CARD_H = 427;
  private static readonly TON_CARD_GAP = 21;
  private static readonly TON_COLS = 3;
  private static readonly TON_ROW_GAP = 24;
  private static readonly CONTENT_BOTTOM_PAD = 20;

  private static readonly ROW_COINS_X = 37;
  private static readonly ROW_COINS_SIZE = 150;
  private static readonly ROW_AMOUNT_X = 200;
  private static readonly ROW_AMOUNT_W = 420;
  private static readonly ROW_BTN_X = 616;
  private static readonly ROW_BTN_Y = 4;
  private static readonly ROW_BTN_W = 366;
  private static readonly ROW_BTN_H = 130;
  private static readonly STAR_ICON_W = 65;
  private static readonly STAR_ICON_H = 62;
  private static readonly STAR_GAP = 6;

  private static readonly CARD_AMOUNT_Y = 37;
  private static readonly CARD_AMOUNT_H = 80;
  private static readonly CARD_COINS_Y = 125;
  private static readonly CARD_COINS_SIZE = 150;
  private static readonly CARD_BTN_Y = 276;
  private static readonly CARD_BTN_W = 316;
  private static readonly CARD_BTN_H = 130;

  private readonly currency_bar: CurrencyBar;
  private readonly tab_backgrounds: UIImage[] = [];
  private readonly tab_contents: UIScrollableContainer[] = [];
  private reward_text: UITextElement | null = null;
  private wallet_icon: UIImage | null = null;
  private wallet_badge: UIImage | null = null;
  private wallet_connected = false;
  private wallet_busy = false;
  private wallet_unsubscribe: ( () => void ) | null = null;
  private buy_scroll: UIScrollableContainer | null = null;
  private buy_holder: UIElementHolder | null = null;
  private purchase_in_progress: string | null = null;
  private readonly shown_offer_ids = new Set<string>();
  private readonly on_ton_order_changed = ( event: Event ): void => this.onTonOrderChanged( event );
  private active_tab = 0;
  private next_currency_sync = 0;
  private closed = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: any,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.wallet_connected = params?.wallet_connected === true;

    this.createBackground();
    this.createHeader();
    this.createAwning();

    const bar_position = CurrencyBar.defaultPosition();
    this.currency_bar = new CurrencyBar( ctx, bar_position.x, bar_position.y );
    this.addChild( this.currency_bar );

    this.createTabs();
    this.createContents();
    this.createBottomBar();
    this.createCloseButton();
    this.syncWalletIcon();

    window.addEventListener( "tonOrderChanged", this.on_ton_order_changed );
    void OfferTriggerController.report( "shop_opened" );

    void InviteService.refresh().then( summary =>
    {
      if ( !summary || this.closed )
        return;

      this.reward_text?.setText( `+${summary.reward_coins}` );
      this.tab_contents.forEach( scroll => scroll.invalidateContentCache() );
    } );
    // Fresh catalog and limits; the cached list is drawn right away.
    void OfferService.refresh().then( () =>
    {
      if ( this.closed ) return;
      this.syncWalletIcon();
      this.rebuildBuyContent();
    } );
  }

  public update( now: number ): void
  {
    super.update( now );

    if ( now < this.next_currency_sync )
      return;

    this.next_currency_sync = now + ShopOverlay.CURRENCY_SYNC_MS;
    this.currency_bar.sync();
  }

  public setWalletConnected( connected: boolean ): void
  {
    if ( connected === this.wallet_connected )
      return;

    this.wallet_connected = connected;
    this.syncWalletBadge();
    this.forceMakeShouldBeRendered();
  }

  public onOverlayWillClose(): void
  {
    if ( this.closed )
      return;

    this.closed = true;
    window.removeEventListener( "tonOrderChanged", this.on_ton_order_changed );
    this.wallet_unsubscribe?.();
    this.wallet_unsubscribe = null;
    void OfferTriggerController.report( "shop_closed" );
    this.params?.onShopClosed?.();
  }

  private static d( design_px: number ): number
  {
    return LayoutScaler.widthFromDesign( design_px );
  }

  private fullWidth(): number
  {
    return this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
  }

  private createBackground(): void
  {
    const x = -Game.safeAreaLeft();
    const y = -Game.safeAreaTop();
    const w = this.fullWidth();
    const h = this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom();

    this.addChild( new RoundedRectangle( this.ctx, x, y, w, h, 0, ShopOverlay.BG_COLOR ) );
  }

  private createHeader(): void
  {
    const x = -Game.safeAreaLeft();
    const y = ShopOverlay.d( ShopOverlay.HEADER_Y );
    const w = this.fullWidth();
    const h = ShopOverlay.d( ShopOverlay.HEADER_H );

    this.addChild( new UINineSliceElement( this.ctx, x, y, w, h, ImgID.UI_FriendsHeaderBg, "friends_header_bg" ) );
  }

  private createAwning(): void
  {
    const tile_w = ShopOverlay.d( ShopOverlay.TOP_IMG_W );
    const tile_h = ShopOverlay.d( ShopOverlay.TOP_IMG_H );
    const stripe_w = ShopOverlay.d( ShopOverlay.TOP_STRIPE_W );
    const center_x = this.rect.getW() / 2;
    const tiles_left = Math.ceil( ( center_x + Game.safeAreaLeft() ) / tile_w );
    const x = center_x - stripe_w / 2 - tiles_left * tile_w;
    const w = this.rect.getW() + Game.safeAreaRight() - x;

    const awning = new UITileImage( this.ctx, x, 0, w, tile_h, ImgID.UI_ShopOverlayTop, tile_w );
    awning.setRepeatStep( tile_w );
    this.addChild( awning );
  }

  private createTabs(): void
  {
    const tab_w = ShopOverlay.d( ShopOverlay.TAB_W );
    const tab_h = ShopOverlay.d( ShopOverlay.TAB_H );
    const tab_y = ShopOverlay.d( ShopOverlay.TAB_Y );
    const group_x = ( this.rect.getW() - tab_w * 2 ) / 2;
    const label_y = ShopOverlay.d( ShopOverlay.TAB_LABEL_Y );
    const label_h = ShopOverlay.d( ShopOverlay.TAB_LABEL_H );
    const keys = [ "SHOP_OVERLAY.TAB.GET", "SHOP_OVERLAY.TAB.BUY" ];

    keys.forEach( ( localization_key, i ) =>
    {
      const tab_x = group_x + i * tab_w;
      const tab = new UIElementHolder( this.ctx, tab_x, tab_y, tab_w, tab_h, false, false );
      tab.setOnClick( () => this.setActiveTab( i ) );

      const background = new UIImage( this.ctx, 0, 0, tab_w, tab_h, ImgID.UI_FriendsTab );
      background.setClickable( false );
      tab.addChild( background, true );
      this.tab_backgrounds.push( background );

      const label = new UITextElement( this.ctx, 0, label_y, tab_w, label_h, {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( ShopOverlay.TAB_FONT ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.friends_tab_outline,
        auto_wrap: false,
        scale_to_width: true,
        ...TextStyleScheme.buttonShadow(),
      } );
      label.setClickable( false );
      tab.addChild( label, true );

      this.addChild( tab );
    } );

    this.syncTabs();
  }

  private setActiveTab( index: number ): void
  {
    if ( index === this.active_tab )
      return;

    void audioController.playSound( AudioClips.UI_CLICK );

    this.removeChild( this.tab_contents[this.active_tab] );
    this.active_tab = index;
    this.addChild( this.tab_contents[this.active_tab] );

    this.syncTabs();
    this.trackShownOffers();
    this.forceMakeShouldBeRendered();
  }

  private syncTabs(): void
  {
    this.tab_backgrounds.forEach( ( background, i ) =>
    {
      const is_active = i === this.active_tab;
      const is_left = i === 0;

      background.setImageId( is_active ? ImgID.UI_FriendsTabActive : ImgID.UI_FriendsTab );
      background.setFlipX( is_left !== is_active );
    } );
  }

  private createContents(): void
  {
    const top = ShopOverlay.d( ShopOverlay.CONTENT_TOP );
    const bottom = this.rect.getH() - ShopOverlay.d( ShopOverlay.BOTTOM_H );
    const w = this.rect.getW();
    const h = Math.max( 1, bottom - top );

    const builders = [
      { build: ( holder: UIElement ) => this.createGetContent( holder ), centered: true },
      { build: ( holder: UIElement ) => this.createBuyContent( holder ), centered: false },
    ];

    builders.forEach( ( { build, centered }, index ) =>
    {
      const scroll = new UIScrollableContainer( this.ctx, 0, top, w, h, {
        direction: "vertical",
        scroll_speed: 1,
        scroll_sensitivity: 0.9,
        enable_momentum: true,
        enable_touch_scroll: true,
        enable_rubber_band: false,
        cache_content: true,
      } );

      const holder = new UIElementHolder( this.ctx, 0, 0, w, h, false, false );
      const content_h = build( holder );
      const centering = centered ? Math.max( 0, ( h - content_h ) / 2 ) : 0;

      holder.getRect().resize( w, content_h );
      holder.setPixelPos( new Vector2( 0, centering ), true );
      scroll.addScrollableChild( holder );
      scroll.setContentHeight( content_h + centering );
      this.tab_contents.push( scroll );
      if ( index === 1 )
      {
        this.buy_scroll = scroll;
        this.buy_holder = holder;
      }
    } );

    this.addChild( this.tab_contents[this.active_tab] );
  }

  /** Redraws the Buy tab from the current catalog (after a refresh, a purchase or a finished crypto order). */
  private rebuildBuyContent(): void
  {
    const scroll = this.buy_scroll;
    if ( !scroll || !this.buy_holder )
      return;

    scroll.removeScrollableChild( this.buy_holder );

    const w = this.rect.getW();
    const h = scroll.getRect().getH();
    const holder = new UIElementHolder( this.ctx, 0, 0, w, h, false, false );
    const content_h = this.createBuyContent( holder );

    holder.getRect().resize( w, content_h );
    holder.setPixelPos( new Vector2( 0, 0 ), true );
    scroll.addScrollableChild( holder );
    scroll.setContentHeight( content_h );
    this.buy_holder = holder;

    this.trackShownOffers();
    this.forceMakeShouldBeRendered();
  }

  private createGetContent( holder: UIElement ): number
  {
    const panel_w = ShopOverlay.d( ShopOverlay.PANEL_W );
    const panel_h = ShopOverlay.d( ShopOverlay.PANEL_H );
    const panel_x = ( this.rect.getW() - panel_w ) / 2;
    const panel_y = ShopOverlay.d( ShopOverlay.PANEL_Y );

    const panel = new UINineSliceElement( this.ctx, panel_x, panel_y, panel_w, panel_h, ImgID.UI_CapsuleBlue, "capsule_bg" );
    holder.addChild( panel );

    const pic_w = ShopOverlay.d( ShopOverlay.FRIENDS_IMG_W );
    const pic_h = ShopOverlay.d( ShopOverlay.FRIENDS_IMG_H );
    const pic_x = ( panel_w - pic_w ) / 2;
    const pic_y = ShopOverlay.d( ShopOverlay.FRIENDS_IMG_Y );

    const pic = new UIImage( this.ctx, pic_x, pic_y, pic_w, pic_h, ImgID.UI_ShopOverlayFriends );
    pic.setClickable( false );
    panel.addChild( pic );

    const text_w = ShopOverlay.d( ShopOverlay.INVITE_TEXT_W );
    const text_h = ShopOverlay.d( ShopOverlay.INVITE_TEXT_H );
    const text_x = ( panel_w - text_w ) / 2;
    const text_y = ShopOverlay.d( ShopOverlay.INVITE_TEXT_Y );

    panel.addChild( new UITextElement( this.ctx, text_x, text_y, text_w, text_h, {
      localization_key: "INVITE_FRIEND.TEXT",
      font_size: LayoutScaler.textSizeFromDesign( 60 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: true,
      outline_color: ColorScheme.invite_friend_text_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
    } ) );

    this.createInviteReward( panel, panel_w );
    this.createInviteButton( panel, panel_w );

    return panel_y + panel_h + ShopOverlay.d( ShopOverlay.CONTENT_BOTTOM_PAD );
  }

  private createInviteReward( panel: UIElement, panel_w: number ): void
  {
    const amount_w = ShopOverlay.d( ShopOverlay.REWARD_TEXT_W );
    const coins_size = ShopOverlay.d( ShopOverlay.REWARD_COINS_SIZE );
    const gap = ShopOverlay.d( ShopOverlay.REWARD_GAP );
    const row_y = ShopOverlay.d( ShopOverlay.REWARD_Y );
    const row_h = ShopOverlay.d( ShopOverlay.REWARD_H );
    const row_x = ( panel_w - amount_w - gap - coins_size ) / 2;
    const reward_coins = InviteService.get()?.reward_coins ?? ShopOverlay.DEFAULT_REWARD_COINS;

    this.reward_text = new UITextElement( this.ctx, row_x, row_y, amount_w, row_h, {
      text: `+${reward_coins}`,
      font_size: LayoutScaler.textSizeFromDesign( 120 ),
      font_weight: 900,
      text_style: TextStyleScheme.rubik,
      text_color: ColorScheme.friends_invite_reward,
      text_align: MyTextAlign.Right,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
      outline_color: ColorScheme.friends_invite_reward_outline,
      scale_to_width: true,
      auto_wrap: false,
    } );
    panel.addChild( this.reward_text );

    const coins_x = row_x + amount_w + gap;
    const coins_y = row_y + ( row_h - coins_size ) / 2;

    const coins = new UIImage( this.ctx, coins_x, coins_y, coins_size, coins_size, ImgID.UI_ShopBlockImg );
    coins.setClickable( false );
    panel.addChild( coins );
  }

  private createInviteButton( panel: UIElement, panel_w: number ): void
  {
    const btn_w = ShopOverlay.d( ShopOverlay.INVITE_BTN_W );
    const btn_h = ShopOverlay.d( ShopOverlay.INVITE_BTN_H );
    const btn_x = ( panel_w - btn_w ) / 2;
    const btn_y = ShopOverlay.d( ShopOverlay.INVITE_BTN_Y );

    const btn = this.createGreenButton( btn_x, btn_y, btn_w, btn_h );
    btn.addChild( new UITextElement( this.ctx, 0, 0, btn_w, btn_h, {
      localization_key: "INVITE_FRIEND.BUTTON",
      font_size: LayoutScaler.textSizeFromDesign( 70 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      scale_to_width: true,
      auto_wrap: false,
      outline_color: ColorScheme.menu_button_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      ...TextStyleScheme.buttonShadow(),
    } ), true );
    btn.setOnClick( () => void InviteService.share() );
    panel.addChild( btn );
  }

  // ---------------------------------------------------------------------------
  // Offers
  // ---------------------------------------------------------------------------

  /** Paid shop offers of the server catalog, best first. */
  private shopOffers(): OfferDto[]
  {
    return OfferService.getOffers()
      .filter( offer => offer.enabled && !offer.chain
        && ( offer.presentation ?? offer.kind ?? "static_shop" ) === "static_shop"
        && !OfferService.isFreeOffer( offer.id ) && !OfferService.isRewardedOffer( offer.id ) )
      .sort( ( a, b ) => b.priority - a.priority );
  }

  private starsOffers(): OfferDto[]
  {
    if ( !TelegramStars.isAvailable() )
      return [];

    return this.shopOffers().filter( offer => !!offer.products?.telegram?.productId && ( offer.products.telegram.priceStars ?? 0 ) > 0 );
  }

  private cryptoOffers(): OfferDto[]
  {
    if ( !TonPayments.isAvailable() )
      return [];

    return this.shopOffers().filter( offer => !!offer.products?.ton?.productId && !!TonPayments.getPrices( offer.products.ton.productId ) );
  }

  /** Coins first; an offer without coins shows its first numeric reward. */
  private static rewardAmount( offer: OfferDto ): number
  {
    const rewards = offer.rewards ?? {};
    if ( typeof rewards.coins === "number" && rewards.coins > 0 )
      return rewards.coins;

    for ( const value of Object.values( rewards ) )
      if ( typeof value === "number" && value > 0 ) return value;

    return 0;
  }

  private static isSoldOut( offer: OfferDto ): boolean
  {
    const limit = OfferService.getPurchaseLimitState( offer );
    return !!limit && limit.left <= 0;
  }

  private analyticsContext( offer: OfferDto ): Record<string, unknown>
  {
    return {
      placement: OfferPlacement.Shop,
      placement_id: OfferPlacement.Shop,
      shop_section: offer.shop_section ?? "gold",
      ...( ShopOverlay.isSoldOut( offer ) ? { locked: true } : {} ),
    };
  }

  private trackShownOffers(): void
  {
    if ( this.active_tab !== 1 )
      return;

    for ( const offer of [ ...this.starsOffers(), ...this.cryptoOffers() ] )
    {
      if ( this.shown_offer_ids.has( offer.id ) ) continue;
      this.shown_offer_ids.add( offer.id );
      void OfferService.trackShown( offer.id, this.analyticsContext( offer ) );
    }
  }

  private createBuyContent( holder: UIElement ): number
  {
    const stars = this.starsOffers();
    const crypto = this.cryptoOffers();
    let cursor = ShopOverlay.STARS_HEADER_Y;

    if ( stars.length )
    {
      this.createSectionHeader( holder, cursor, "SHOP_OVERLAY.FOR_STARS" );
      cursor += ShopOverlay.HEADER_TO_ITEMS;

      const row_step = ShopOverlay.STARS_ROW_H + ShopOverlay.STARS_ROW_GAP;
      stars.forEach( ( offer, i ) => this.createStarsRow( holder, cursor + i * row_step, offer ) );
      cursor += stars.length * row_step - ShopOverlay.STARS_ROW_GAP + ShopOverlay.SECTION_GAP;
    }

    if ( crypto.length )
    {
      this.createSectionHeader( holder, cursor, "SHOP_OVERLAY.FOR_TON" );
      cursor += ShopOverlay.HEADER_TO_ITEMS;

      const cols = ShopOverlay.TON_COLS;
      const rows = Math.ceil( crypto.length / cols );
      const card_w = ShopOverlay.d( ShopOverlay.TON_CARD_W );
      const card_gap = ShopOverlay.d( ShopOverlay.TON_CARD_GAP );
      const cards_w = cols * card_w + ( cols - 1 ) * card_gap;
      const cards_x = ( this.rect.getW() - cards_w ) / 2;
      const card_row_step = ShopOverlay.TON_CARD_H + ShopOverlay.TON_ROW_GAP;

      crypto.forEach( ( offer, i ) =>
      {
        const x = cards_x + ( i % cols ) * ( card_w + card_gap );
        this.createTonCard( holder, x, cursor + Math.floor( i / cols ) * card_row_step, offer );
      } );
      cursor += rows * ShopOverlay.TON_CARD_H + ( rows - 1 ) * ShopOverlay.TON_ROW_GAP + ShopOverlay.SECTION_GAP;
    }

    if ( !stars.length && !crypto.length )
    {
      const w = ShopOverlay.d( ShopOverlay.SECTION_HEADER_W );
      const text = new UITextElement( this.ctx, ( this.rect.getW() - w ) / 2, ShopOverlay.d( cursor ), w, ShopOverlay.d( ShopOverlay.EMPTY_TEXT_H ), {
        localization_key: "SHOP_OVERLAY.EMPTY",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.shop_amount_outline,
        auto_wrap: true,
      } );
      text.setClickable( false );
      holder.addChild( text );
      cursor += ShopOverlay.EMPTY_TEXT_H;
    }

    return ShopOverlay.d( cursor + ShopOverlay.CONTENT_BOTTOM_PAD );
  }

  private createSectionHeader( holder: UIElement, design_y: number, localization_key: string ): void
  {
    const w = ShopOverlay.d( ShopOverlay.SECTION_HEADER_W );
    const h = ShopOverlay.d( ShopOverlay.SECTION_HEADER_H );
    const x = ( this.rect.getW() - w ) / 2;
    const y = ShopOverlay.d( design_y );
    const text_pad = ShopOverlay.d( 110 );
    const text_w = w - text_pad * 2;

    const header = new UINineSliceElement( this.ctx, x, y, w, h, ImgID.UI_ShopOverlayItemHeader, "shop_overlay_item_header" );
    header.addChild( new UITextElement( this.ctx, text_pad, 0, text_w, h, {
      localization_key,
      font_size: LayoutScaler.textSizeFromDesign( 50 ),
      font_weight: 900,
      text_style: TextStyleScheme.rubik,
      text_color: ColorScheme.shop_title_text,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.shop_title_outline,
      scale_to_width: true,
      auto_wrap: false,
      using_y_offset: false
    } ), true );
    holder.addChild( header );
  }

  private createStarsRow( holder: UIElement, design_y: number, offer: OfferDto ): void
  {
    const row_w = ShopOverlay.d( ShopOverlay.STARS_ROW_W );
    const row_h = ShopOverlay.d( ShopOverlay.STARS_ROW_H );
    const row_x = ( this.rect.getW() - row_w ) / 2;
    const row_y = ShopOverlay.d( design_y );

    const row = new UINineSliceElement( this.ctx, row_x, row_y, row_w, row_h, ImgID.UI_ShopOverlayStarsBg, "shop_overlay_stars_bg" );
    holder.addChild( row );

    const coins_size = ShopOverlay.d( ShopOverlay.ROW_COINS_SIZE );
    const coins_x = ShopOverlay.d( ShopOverlay.ROW_COINS_X );
    const coins_y = ( row_h - coins_size ) / 2;

    const coins = new UIImage( this.ctx, coins_x, coins_y, coins_size, coins_size, ImgID.UI_ShopBlockImg );
    coins.setClickable( false );
    row.addChild( coins );

    const amount_x = ShopOverlay.d( ShopOverlay.ROW_AMOUNT_X );
    const amount_w = ShopOverlay.d( ShopOverlay.ROW_AMOUNT_W );

    row.addChild( this.createAmountText( amount_x, 0, amount_w, row_h, ShopOverlay.rewardAmount( offer ), MyTextAlign.Left, 70 ) );

    const btn_x = ShopOverlay.d( ShopOverlay.ROW_BTN_X );
    const btn_y = ShopOverlay.d( ShopOverlay.ROW_BTN_Y );
    const btn_w = ShopOverlay.d( ShopOverlay.ROW_BTN_W );
    const btn_h = ShopOverlay.d( ShopOverlay.ROW_BTN_H );

    const btn = this.createGreenButton( btn_x, btn_y, btn_w, btn_h );
    const price_text = ShopOverlay.isSoldOut( offer )
      ? this.addSoldOut( btn, btn_w, btn_h, 40 )
      : this.addStarsPrice( btn, btn_w, btn_h, offer.products.telegram?.priceStars ?? 0 );
    this.bindBuyButton( btn, offer, price_text, OfferStore.TelegramStars );
    row.addChild( btn );
  }

  private addStarsPrice( btn: UIElement, btn_w: number, btn_h: number, stars: number ): UITextElement
  {
    const icon_w = ShopOverlay.d( ShopOverlay.STAR_ICON_W );
    const icon_h = ShopOverlay.d( ShopOverlay.STAR_ICON_H );
    const gap = ShopOverlay.d( ShopOverlay.STAR_GAP );
    const center_x = btn_w / 2;
    const text_w = center_x - gap;
    const icon_x = center_x;
    const icon_y = ( btn_h - icon_h ) / 2;

    const price_text = this.createPriceText( 0, 0, text_w, btn_h, String( stars ), MyTextAlign.Right, 45 );
    btn.addChild( price_text, true );

    const icon = new UIImage( this.ctx, icon_x, icon_y, icon_w, icon_h, ImgID.UI_ShopOverlayStarsIcon );
    icon.setClickable( false );
    btn.addChild( icon, true );

    return price_text;
  }

  private addSoldOut( btn: UIElement, btn_w: number, btn_h: number, font: number ): UITextElement
  {
    const text = this.createPriceText( 0, 0, btn_w, btn_h, "", MyTextAlign.Center, font );
    text.setLocalizationKey( "SHOP_OVERLAY.SOLD_OUT" );
    btn.addChild( text, true );
    btn.setImageOpacity( 0.55 );
    return text;
  }

  private createTonCard( holder: UIElement, x: number, design_y: number, offer: OfferDto ): void
  {
    const card_w = ShopOverlay.d( ShopOverlay.TON_CARD_W );
    const card_h = ShopOverlay.d( ShopOverlay.TON_CARD_H );
    const card_y = ShopOverlay.d( design_y );

    const card = new UIImage( this.ctx, x, card_y, card_w, card_h, ImgID.UI_ShopOverlayTonBg );
    holder.addChild( card );

    const amount_y = ShopOverlay.d( ShopOverlay.CARD_AMOUNT_Y );
    const amount_h = ShopOverlay.d( ShopOverlay.CARD_AMOUNT_H );

    card.addChild( this.createAmountText( 0, amount_y, card_w, amount_h, ShopOverlay.rewardAmount( offer ), MyTextAlign.Center, 70 ) );

    const coins_size = ShopOverlay.d( ShopOverlay.CARD_COINS_SIZE );
    const coins_x = ( card_w - coins_size ) / 2;
    const coins_y = ShopOverlay.d( ShopOverlay.CARD_COINS_Y );

    const coins = new UIImage( this.ctx, coins_x, coins_y, coins_size, coins_size, ImgID.UI_ShopBlockImg );
    coins.setClickable( false );
    card.addChild( coins );

    const btn_w = ShopOverlay.d( ShopOverlay.CARD_BTN_W );
    const btn_h = ShopOverlay.d( ShopOverlay.CARD_BTN_H );
    const btn_x = ( card_w - btn_w ) / 2;
    const btn_y = ShopOverlay.d( ShopOverlay.CARD_BTN_Y );

    const btn = this.createGreenButton( btn_x, btn_y, btn_w, btn_h );
    let price_text: UITextElement;
    if ( ShopOverlay.isSoldOut( offer ) )
    {
      price_text = this.addSoldOut( btn, btn_w, btn_h, 40 );
    }
    else
    {
      // One price on the card (USDT when sold for it, else TON); the payment sheet offers both assets.
      const product_id = offer.products.ton?.productId ?? "";
      const prices = TonPayments.getPrices( product_id );
      const asset = TonPayments.defaultAsset( product_id );
      const label = prices && asset ? TonPayments.formatAsset( asset, asset === "TON" ? prices.ton! : prices.usdt! ) : "";
      price_text = this.createPriceText( 0, 0, btn_w, btn_h, label, MyTextAlign.Center, 44 );
      btn.addChild( price_text, true );
    }
    this.bindBuyButton( btn, offer, price_text, OfferStore.Ton );
    card.addChild( btn );
  }

  private bindBuyButton( btn: UIElement, offer: OfferDto, price_text: UITextElement, store: OfferStore ): void
  {
    if ( ShopOverlay.isSoldOut( offer ) )
    {
      btn.setClickable( false );
      return;
    }
    btn.setOnClick( () => void this.purchase( offer, price_text, store ) );
  }

  /** Stars rows and crypto cards can share one screen (Telegram), so the card names its store. */
  private async purchase( offer: OfferDto, price_text: UITextElement, store: OfferStore ): Promise<void>
  {
    if ( this.purchase_in_progress )
      return;

    this.purchase_in_progress = offer.id;
    void audioController.playSound( AudioClips.UI_CLICK );
    const previous_text = price_text.getText();
    price_text.setText( "..." );
    this.buy_scroll?.invalidateContentCache();
    this.forceMakeShouldBeRendered();

    try
    {
      await OfferService.trackClicked( offer.id, this.analyticsContext( offer ) );
      const validation = await OfferService.purchaseOffer( offer.id, this.analyticsContext( offer ), undefined, { store } );
      await this.applyPurchaseEconomy( validation.player );
      // Limits and the catalog may have changed; the row is rebuilt, so the button text need not be restored.
      this.rebuildBuyContent();
    }
    catch ( e )
    {
      price_text.setText( previous_text );
      this.buy_scroll?.invalidateContentCache();
      this.forceMakeShouldBeRendered();
      // A crypto payment still confirming on-chain is not a failure: the tracker finishes it (see onTonOrderChanged).
      if ( e instanceof TonPaymentPending ) return;
      if ( e instanceof TonConnectFailure && e.reason === "cancelled_by_user" ) return;
      console.warn( "[ShopOverlay] purchase failed", e );
      crashCapture( e, { phase: "shop_overlay_purchase", offer_id: offer.id } );
    }
    finally
    {
      this.purchase_in_progress = null;
    }
  }

  private async applyPurchaseEconomy( player?: PlayerDto | null ): Promise<void>
  {
    const coins_before = StatisticsStorage.load().coins;
    if ( player ) adoptEconomyFromPlayer( player );
    else await refreshPlayerFromServer();

    const coins_earned = Math.max( 0, StatisticsStorage.load().coins - coins_before );
    void logVirtualCurrencyEarned( { currency_name: "coins", amount: coins_earned, source: "iap_purchase" } );
    this.currency_bar.sync();
  }

  /** A crypto order finished in the background (the tracker already adopted the player): redraw limits and coins. */
  private onTonOrderChanged( event: Event ): void
  {
    if ( this.closed )
      return;

    const order = ( event as CustomEvent ).detail;
    if ( order?.status !== "fulfilled" )
      return;

    this.currency_bar.sync();
    this.rebuildBuyContent();
  }

  private createGreenButton( x: number, y: number, w: number, h: number ): UINineSliceElement
  {
    return new UINineSliceElement( this.ctx, x, y, w, h, ImgID.UI_GreenFrameBtn, "green_frame_btn", undefined, undefined, true );
  }

  private createAmountText( x: number, y: number, w: number, h: number, coins: number, align: MyTextAlign, font: number ): UITextElement
  {
    const text = new UITextElement( this.ctx, x, y, w, h, {
      text: `+${coins}`,
      font_size: LayoutScaler.textSizeFromDesign( font ),
      font_weight: 900,
      text_style: TextStyleScheme.rubik,
      text_color: ColorScheme.white,
      text_align: align,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
      outline_color: ColorScheme.shop_amount_outline,
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      scale_to_width: true,
      auto_wrap: false,
    } );
    text.setClickable( false );

    return text;
  }

  private createPriceText( x: number, y: number, w: number, h: number, value: string, align: MyTextAlign, font: number ): UITextElement
  {
    const text = new UITextElement( this.ctx, x, y, w, h, {
      text: value,
      font_size: LayoutScaler.textSizeFromDesign( font ),
      font_weight: 900,
      text_style: TextStyleScheme.rubik,
      text_color: ColorScheme.white,
      text_align: align,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_color: ColorScheme.menu_button_outline,
      scale_to_width: true,
      auto_wrap: false,
    } );
    text.setClickable( false );

    return text;
  }

  private createBottomBar(): void
  {
    const bar_h = ShopOverlay.d( ShopOverlay.BOTTOM_H );
    const x = -Game.safeAreaLeft();
    const y = this.rect.getH() - bar_h;
    const w = this.fullWidth();
    const fill_y = this.rect.getH() - 1;
    const fill_h = Game.safeAreaBottom() + 1;

    this.addChild( new UINineSliceElement( this.ctx, x, y, w, bar_h, ImgID.UI_ShopOverlayBottom, "shop_overlay_bottom" ) );
    this.addChild( new RoundedRectangle( this.ctx, x, fill_y, w, fill_h, 0, ShopOverlay.BOTTOM_FILL_COLOR ) );
  }

  private createCloseButton(): void
  {
    const size = ShopOverlay.d( ShopOverlay.CLOSE_SIZE );
    const x = ( this.rect.getW() - size ) / 2;
    const y = this.rect.getH() - ShopOverlay.d( ShopOverlay.CLOSE_CENTER_FROM_BOTTOM ) - size / 2;

    const close = new UIImage( this.ctx, x, y, size, size, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    close.setOnClick( () => OverlayManager.closeOverlay() );
    this.addChild( close );
  }

  /** The wallet button exists only where crypto checkout does (web outside Telegram, crypto prices loaded). */
  private syncWalletIcon(): void
  {
    if ( this.wallet_icon || !TonPayments.isAvailable() )
      return;

    const size = ShopOverlay.d( ShopOverlay.WALLET_SIZE );
    const x = this.rect.getW() - ShopOverlay.d( ShopOverlay.WALLET_RIGHT_MARGIN ) - size;
    const center_y = this.rect.getH() - ShopOverlay.d( ShopOverlay.CLOSE_CENTER_FROM_BOTTOM );
    const y = center_y - size / 2;

    this.wallet_icon = new UIImage( this.ctx, x, y, size, size, ImgID.UI_ShopOverlayWallet, {}, true );
    this.wallet_icon.setOnClick( () => void this.onWalletClick() );
    this.addChild( this.wallet_icon );

    const badge_w = ShopOverlay.d( ShopOverlay.WALLET_BADGE_W );
    const badge_h = ShopOverlay.d( ShopOverlay.WALLET_BADGE_H );
    const badge_x = x + size / 2 + ShopOverlay.d( ShopOverlay.WALLET_BADGE_DX ) - badge_w / 2;
    const badge_y = center_y + ShopOverlay.d( ShopOverlay.WALLET_BADGE_DY ) - badge_h / 2;

    this.wallet_badge = new UIImage( this.ctx, badge_x, badge_y, badge_w, badge_h, ImgID.UI_ShopOverlayWalletDisconnected, {}, false );
    this.wallet_badge.setClickable( false );
    this.addChild( this.wallet_badge );

    this.syncWalletBadge();
    // Badge follows TON Connect: session restore on load, connect / disconnect from anywhere.
    this.wallet_unsubscribe = TonPayments.onStatusChange( address => this.setWalletConnected( !!address ) );
    this.forceMakeShouldBeRendered();
  }

  /** Disconnected: open the TON Connect wallet picker. Connected: show the address with a Disconnect button. */
  private async onWalletClick(): Promise<void>
  {
    if ( typeof this.params?.onWalletClick === "function" )
    {
      this.params.onWalletClick( this.wallet_connected );
      return;
    }
    if ( this.wallet_busy )
      return;

    this.wallet_busy = true;
    void audioController.playSound( AudioClips.UI_CLICK );
    try
    {
      if ( this.wallet_connected )
      {
        const raw = await TonPayments.connectedAddress();
        if ( !raw )
        {
          this.setWalletConnected( false );
          return;
        }
        TonPaymentSheet.show( { kind: "wallet", address: await TonPayments.friendlyAddress( raw ), testnet: ( await TonPayments.connectedChain() ) === "-3" } );
        const action = await TonPaymentSheet.waitForAction();
        if ( action === "disconnect" )
        {
          await TonPayments.disconnect();
          this.setWalletConnected( false );
        }
        TonPaymentSheet.close();
        return;
      }

      TonPaymentSheet.show( { kind: "connecting" } );
      TonPaymentSheet.onCancel( () => TonPayments.cancelPending() );
      await TonPayments.connect();
      this.setWalletConnected( true );
      TonPaymentSheet.close();
    }
    catch ( e )
    {
      TonPaymentSheet.close();
      if ( e instanceof TonConnectFailure && e.reason === "cancelled_by_user" ) return;
      console.warn( "[ShopOverlay] wallet connect failed", e );
    }
    finally
    {
      this.wallet_busy = false;
    }
  }

  private syncWalletBadge(): void
  {
    this.wallet_badge?.setImageId( this.wallet_connected ? ImgID.UI_ShopOverlayWalletConnected : ImgID.UI_ShopOverlayWalletDisconnected );
  }

  public name(): string
  {
    return "ShopOverlay";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();

    SafeAreas.renderTopSafeAreaOverlay( this.ctx );
  }
}
